/**
 * Build Component — turn drawn geometry into a reusable part.
 *
 * The manual's way of making a part is to draw it and then declare it one
 * (Place ▸ Build Component, Block ▸ Build Component): the selected lines, arcs,
 * pads and text become a component, and the component goes into a library so it
 * can be placed again. This is the dialog that names it.
 *
 * Saving under the name of an existing part replaces it, which is also how a
 * part is *edited*: place it, change it, build it again.
 */

import type { DocumentKind } from '@sircad/core/model.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface BuildPartDetails {
  name: string;
  /** Reference designator template, e.g. `U?`. Blank for a footprint. */
  refPrefix: string;
  /** Footprint this symbol maps to, for the netlist. Blank for a footprint. */
  footprint: string;
  category: string;
  description: string;
  /** Keep the geometry as a component in the drawing, not just in the library. */
  replaceInPlace: boolean;
}

export interface BuildPartOptions {
  kind: DocumentKind;
  /** How many items are being built into the part, for the prompt. */
  count: number;
  /** Suggested name, e.g. from a part already selected. */
  suggested?: string;
  onConfirm(details: BuildPartDetails): void;
}

export function showBuildPartDialog(opts: BuildPartOptions): void {
  const isSymbol = opts.kind === 'sch';

  openDialog({
    title: isSymbol ? 'Build Symbol' : 'Build Footprint',
    build(body, handle) {
      const form = document.createElement('form');
      form.className = 'modal-form';
      body.append(form);

      const name = text('Name', opts.suggested ?? '');
      const ref = isSymbol ? text('Ref prefix', 'U?') : null;
      const footprint = isSymbol ? text('Footprint', '') : null;
      const category = text('Category', isSymbol ? 'Custom' : 'Custom');
      const description = text('Description', '');

      const replace = document.createElement('input');
      replace.type = 'checkbox';
      replace.checked = true;
      form.append(labelled('Group in drawing', replace));

      const err = document.createElement('p');
      err.className = 'modal-error';
      err.hidden = true;
      form.append(err);

      const note = document.createElement('p');
      note.className = 'modal-hint';
      note.textContent = `${opts.count} item${opts.count === 1 ? '' : 's'} will become `
        + `${isSymbol ? 'a symbol' : 'a footprint'}. Saving under an existing name `
        + 'replaces that part, which is how you edit one.';
      form.append(note);

      let done = false;
      const confirm = (): void => {
        if (done) return;
        const n = name.value.trim();
        if (!n) {
          err.textContent = 'The part needs a name.';
          err.hidden = false;
          name.focus();
          return;
        }
        done = true;
        handle.close();
        opts.onConfirm({
          name: n,
          refPrefix: ref?.value.trim() ?? '',
          footprint: footprint?.value.trim() ?? '',
          category: category.value.trim() || 'Custom',
          description: description.value.trim(),
          replaceInPlace: replace.checked,
        });
      };

      const row = buttons(form);
      button(row, 'Cancel', () => handle.close());
      button(row, 'Build', confirm, true);
      // Clicking away accepts, but only once the part has a name to save under.
      handle.setAccept(() => { if (name.value.trim()) confirm(); else handle.close(); });
      form.addEventListener('submit', (ev) => { ev.preventDefault(); confirm(); });

      queueMicrotask(() => name.focus());

      function text(label: string, value: string): HTMLInputElement {
        const input = document.createElement('input');
        input.type = 'text';
        input.value = value;
        input.spellcheck = false;
        form.append(labelled(label, input));
        return input;
      }
    },
  });
}

function labelled(label: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement('label');
  wrap.className = 'field';
  const span = document.createElement('span');
  span.textContent = label;
  wrap.append(span, control);
  return wrap;
}
